// Kiosk / wall-screen token handoff (face-pass carryover, product glue):
// a ?st_token= in the URL is consumed ONCE into the session and stripped
// before the app mounts — the HMI posture (and what makes one-shot
// headless captures possible). Never logged, never left in the URL.
try {
  const u = new URL(window.location.href);
  const st = u.searchParams.get('st_token');
  if (st) {
    localStorage.setItem('token', st);
    // Kiosk admin surfaces (and the evidence captures): st_admin=1 arms
    // the admin-mode toggle for this session — the server still gates
    // every admin API by group, this only opens the CLIENT toggle.
    if (u.searchParams.get('st_admin') === '1') {
      sessionStorage.setItem(`${STORAGE_PREFIX}_adminMode`, 'true');
    }
    u.searchParams.delete('st_token');
    u.searchParams.delete('st_admin');
    window.history.replaceState(null, '', u.pathname + (u.search || '') + u.hash);
  }
} catch { /* URL parse failure — normal login flow */ }

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@app/App';
import { APP_TITLE, STORAGE_PREFIX } from '@client/config/client.config';
import '@app/index.css';
import { installPhoneViewport } from '@platform/app-shell/frontend/utils/phoneViewport';

// PWA service worker — registered manually (vite.config.js sets
// injectRegister: false) so WE decide what a new deployment does to an open
// page: nothing. A page load is answered by the SERVER first (vite.config.js
// runtimeCaching — the page is no longer precached), so a refresh after a
// deploy already runs the new build. The new worker then installs and takes
// over in the background WITHOUT reloading the page: that reload, onto a
// page that was already current, was the flicker every deploy (owner,
// 2026-10-15). A tab left open across a deploy is told by the update banner
// (App.jsx UpdateBanner) and reloads when the person chooses; a route it
// lazy-loads after the old files are gone recovers once (below).
// /auth/* landings were never reloaded and still are not: invite/reset links
// carry one-time tokens consumed at mount.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* dev server has no sw.js */ });
  });
}

// A deploy renames every hashed chunk, and an established session is
// deliberately NOT force-reloaded (above) — so a tab can outlive the build it
// was served by. It survives happily until it lazy-loads a route, asks for a
// chunk that no longer exists, and dies with a blank screen: the route never
// mounts and React has nothing to render.
//
// Vite raises vite:preloadError for exactly this. One reload lands the tab on
// the current shell and the route loads normally. The marker makes it ONE
// reload: if the new shell fails the same way the failure is real, and it must
// surface as an error rather than a reload loop. It is cleared only once the
// page has RUN a while — not on the load event, which fires before the routes
// ask for their chunks, so a failing build reloaded, cleared, failed and
// reloaded again: the endless flicker after a deploy (diagnosed 2026-10-12).
const RELOAD_MARK = 'stride:chunk-reload';
window.addEventListener('vite:preloadError', (event) => {
  let tried = false;
  // Storage can throw outright (private mode, blocked site data). A tab that
  // cannot remember is still better off reloading once than sitting blank.
  try { tried = !!sessionStorage.getItem(RELOAD_MARK); } catch { /* unreadable */ }
  if (tried) return;                                // already tried — let it throw
  try { sessionStorage.setItem(RELOAD_MARK, String(Date.now())); } catch { /* unwritable */ }
  event.preventDefault();
  window.location.reload();
});
const RELOAD_MARK_CLEAR_MS = 10000;
window.addEventListener('load', () => {
  setTimeout(() => {
    try { sessionStorage.removeItem(RELOAD_MARK); } catch { /* private mode */ }
  }, RELOAD_MARK_CLEAR_MS);
});

document.title = APP_TITLE;
console.log('main.jsx: React mounting...');

// Eruda — phone-friendly in-page console. Triggered by adding `?debug=1`
// to the URL. Once activated, it persists in sessionStorage so links
// internal to the app keep the debug overlay alive without re-adding
// the param. No-op for normal users — the script tag is only injected
// when explicitly requested.
(function maybeLoadEruda() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '1') sessionStorage.setItem('zi-debug', '1');
    if (sessionStorage.getItem('zi-debug') !== '1') return;
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/eruda@3';
    s.onload = () => { try { window.eruda?.init(); console.log('[debug] Eruda console loaded'); } catch (e) { console.error('[debug] Eruda init failed', e); } };
    document.head.appendChild(s);
  } catch (e) { /* ignore — debug helper must never break the app */ }
})();

// ───── Phone viewport ─────
// The desktop layout pins panes to viewport edges (body height:100vh,
// tab-panes position:absolute inset:0), so a phone at width=device-width
// sees content cropped at the right. A touch device therefore gets the
// desktop pages at a fixed 1280px viewport, scaled to fit, UNLESS the page
// in front is phone-ready — then it gets its real device width. The rule,
// the list of ready routes and the one writer of the viewport tag live in
// phoneViewport.js.
installPhoneViewport();

// ───── iOS pull-to-refresh guard ─────
// On iOS, releasing one finger after a pinch-out leaves a single-finger
// downward swipe in flight. If that lands at the very top of the page,
// Safari's chrome-level handler interprets it as pull-to-refresh and
// reloads the page, even with overscroll-behavior:none and
// position:fixed body. The CSS gets us 90% of the way; this handler
// catches the residual case by preventDefault'ing the touchmove only
// when (a) we're a touch device, (b) it's a single-touch downward drag,
// and (c) the scrolling element is already at the top. Internal
// scrollable panels (which have their own scroll containers) keep
// working because their scrollTop > 0 once they've scrolled, and their
// touchmove targets aren't the document body.
(function setupPullRefreshGuard() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const isTouch = window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  if (!isTouch) return;

  let touchStartY = null;
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      touchStartY = e.touches[0].clientY;
    } else {
      // Multi-touch (pinch) — let the browser handle it natively.
      touchStartY = null;
    }
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (touchStartY === null || e.touches.length !== 1) return;
    const dy = e.touches[0].clientY - touchStartY;
    if (dy <= 0) return; // upward / sideways drag — leave alone
    // If the touch target sits inside a scrollable element that is itself
    // scrolled (or can scroll), let it through. We only block when there
    // is genuinely no scroll-up to consume the gesture, which is when
    // pull-to-refresh would otherwise trigger.
    const target = e.target;
    let el = target instanceof Element ? target : null;
    while (el) {
      if (el.scrollHeight > el.clientHeight && el.scrollTop > 0) return;
      el = el.parentElement;
    }
    e.preventDefault();
  }, { passive: false });
})();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

console.log('main.jsx: React mounted');
